// src/pages/Seguros.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import {
  FaEdit,
  FaFileExcel,
  FaFileInvoiceDollar,
  FaMoneyBillWave,
  FaPlus,
  FaShieldAlt,
  FaSyncAlt,
  FaTrash,
  FaUndoAlt,
} from "react-icons/fa";

import * as XLSX from "xlsx";

import {
  getPolizasSeguros,
  getPolizaSeguro,
  crearPolizaSeguro,
  editarPolizaSeguro,
  getCuotasSeguros,
  crearCuotaSeguro,
  pagarCuotaSeguro,
  anularPagoCuotaSeguro,
  getCamionesParaSeguros,
} from "../helpers/rutaSeguros";

import {
  getCajas,
} from "../helpers/rutaCajas";

import {
  getCuentas,
} from "../helpers/rutaCuentasBancarias";

import {
  getCarteraDisponibles,
} from "../helpers/rutaCarteras";

/* =========================================================
   HELPERS GENERALES
========================================================= */

const money = (valor) =>
  Number(valor || 0).toLocaleString(
    "es-AR",
    {
      style: "currency",
      currency: "ARS",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  );

const fechaCorta = (valor) => {
  if (!valor) return "-";

  try {
    return new Date(
      valor
    ).toLocaleDateString(
      "es-AR",
      {
        timeZone: "UTC",
      }
    );
  } catch {
    return "-";
  }
};

const fechaInput = (valor) => {
  if (!valor) return "";

  try {
    return new Date(valor)
      .toISOString()
      .slice(0, 10);
  } catch {
    return "";
  }
};

const hoyInput = () =>
  new Date()
    .toISOString()
    .slice(0, 10);

const getCajaLabel = (caja) => {
  if (!caja) return "";

  return (
    caja.descripcion ||
    caja.caja ||
    caja.nombre ||
    `Caja ${caja.codcaj || ""}`
  );
};

const getCuentaLabel = (
  cuenta
) => {
  if (!cuenta) return "";

  return [
    cuenta.alias,
    cuenta.banco,
    cuenta.numero,
  ]
    .filter(Boolean)
    .join(" - ");
};

const normalizarItemsCartera = (
  respuesta
) => {
  if (Array.isArray(respuesta)) {
    return respuesta;
  }

  if (
    Array.isArray(
      respuesta?.items
    )
  ) {
    return respuesta.items;
  }

  if (
    Array.isArray(
      respuesta?.cartera
    )
  ) {
    return respuesta.cartera;
  }

  if (
    Array.isArray(
      respuesta?.elementos
    )
  ) {
    return respuesta.elementos;
  }

  return [];
};

const n = (valor) => {
  const numero =
    Number(valor);

  return Number.isFinite(
    numero
  )
    ? numero
    : 0;
};

const estadoBadge = (
  estado
) => {
  switch (
    String(estado || "")
      .toUpperCase()
  ) {
    case "VIGENTE":
      return "success";

    case "VENCIDA":
      return "danger";

    case "ANULADA":
      return "secondary";

    case "REEMPLAZADA":
      return "dark";

    case "PENDIENTE":
      return "warning";

    default:
      return "secondary";
  }
};

const estadoCuotaBadge = (
  estado
) => {
  switch (
    String(estado || "")
      .toUpperCase()
  ) {
    case "PAGADA":
      return "success";

    case "VENCIDA":
      return "danger";

    case "ANULADA":
      return "secondary";

    default:
      return "warning";
  }
};

/* =========================================================
   HELPERS IMPORTACIÓN EXCEL
========================================================= */

const normalizarPatenteSeguro =
  (valor) =>
    String(valor || "")
      .trim()
      .toUpperCase()
      .replace(
        /[^A-Z0-9]/g,
        ""
      );

const normalizarClaveExcel =
  (valor) =>
    String(valor || "")
      .trim()
      .toUpperCase()
      .normalize("NFD")
      .replace(
        /[\u0300-\u036f]/g,
        ""
      )
      .replace(
        /[^A-Z0-9]/g,
        ""
      );

const numeroExcelSeguro = (
  valor
) => {
  if (
    typeof valor ===
    "number"
  ) {
    return Number.isFinite(
      valor
    )
      ? valor
      : 0;
  }

  let str =
    String(valor || "")
      .trim()
      .replace(/\$/g, "")
      .replace(/\s/g, "");

  if (!str) {
    return 0;
  }

  const coma =
    str.lastIndexOf(",");

  const punto =
    str.lastIndexOf(".");

  if (
    coma > -1 &&
    punto > -1 &&
    coma > punto
  ) {
    str = str
      .replace(/\./g, "")
      .replace(",", ".");
  } else if (
    coma > -1 &&
    punto === -1
  ) {
    str =
      str.replace(
        ",",
        "."
      );
  } else if (
    punto > -1 &&
    coma > -1 &&
    punto > coma
  ) {
    str =
      str.replace(
        /,/g,
        ""
      );
  }

  const numero =
    Number(str);

  return Number.isFinite(
    numero
  )
    ? numero
    : 0;
};

const extraerPeriodoExcel =
  (valor) => {
    const texto =
      String(valor || "");

    const match =
      texto.match(
        /(\d{1,2})[./-](\d{1,2})[./-](\d{4}).*?(\d{1,2})[./-](\d{1,2})[./-](\d{4})/
      );

    if (!match) {
      return null;
    }

    return {
      desde: `${match[1].padStart(
        2,
        "0"
      )}/${match[2].padStart(
        2,
        "0"
      )}/${match[3]}`,

      hasta: `${match[4].padStart(
        2,
        "0"
      )}/${match[5].padStart(
        2,
        "0"
      )}/${match[6]}`,
    };
  };

/* =========================================================
   FORMULARIO PÓLIZA
========================================================= */

const vehiculoVacio = () => ({
  vehiculo: "",
  patente: "",

  descripcionSeguro: "",
  marcaModeloSeguro: "",
  cobertura: "",

  sumaAsegurada: "",
  premio: "",

  observaciones: "",

  estadoVinculacion: "",
  vehiculoInfo: null,
});

const FORM_POLIZA_INICIAL = {
  compania: "",
  nroPoliza: "",
  ramo: "AUTOMOTORES",

  asegurado: "",
  cuitAsegurado: "",
  domicilioAsegurado: "",

  productor: "",
  codigoProductor: "",

  fechaEmision: "",
  vigenciaDesde: "",
  vigenciaHasta: "",

  estado: "VIGENTE",

  moneda: "ARS",

  observaciones: "",

  vehiculos: [
    vehiculoVacio(),
  ],
};

/* =========================================================
   MODAL PÓLIZA
========================================================= */

function ModalPoliza({
  show,
  onHide,
  poliza,
  onSaved,
}) {
  const editando =
    !!poliza?._id;

  const [
    form,
    setForm,
  ] = useState(
    FORM_POLIZA_INICIAL
  );

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    camiones,
    setCamiones,
  ] = useState([]);

  const [
    cargandoCamiones,
    setCargandoCamiones,
  ] = useState(false);

  const [
    importandoExcel,
    setImportandoExcel,
  ] = useState(false);

  const [
    infoImportacion,
    setInfoImportacion,
  ] = useState(null);

  /* =======================================================
     CARGAR CAMIONES
  ======================================================= */

  useEffect(() => {
    if (!show) {
      return;
    }

    const cargarCamiones =
      async () => {
        setCargandoCamiones(
          true
        );

        try {
          const respuesta =
            await getCamionesParaSeguros();

          if (!respuesta?.ok) {
            console.error(
              respuesta?.error
            );

            return;
          }

          setCamiones(
            Array.isArray(
              respuesta.camiones
            )
              ? respuesta.camiones
              : []
          );
        } finally {
          setCargandoCamiones(
            false
          );
        }
      };

    cargarCamiones();
  }, [show]);

  /* =======================================================
     CARGAR DATOS DEL MODAL
  ======================================================= */

  useEffect(() => {
    if (!show) {
      return;
    }

    setError("");
    setInfoImportacion(
      null
    );

    if (!poliza) {
      setForm({
        ...FORM_POLIZA_INICIAL,

        vehiculos: [
          vehiculoVacio(),
        ],
      });

      return;
    }

    setForm({
      compania:
        poliza.compania ||
        "",

      nroPoliza:
        poliza.nroPoliza ||
        "",

      ramo:
        poliza.ramo ||
        "AUTOMOTORES",

      asegurado:
        poliza.asegurado ||
        "",

      cuitAsegurado:
        poliza.cuitAsegurado ||
        "",

      domicilioAsegurado:
        poliza.domicilioAsegurado ||
        "",

      productor:
        poliza.productor ||
        "",

      codigoProductor:
        poliza.codigoProductor ||
        "",

      fechaEmision:
        fechaInput(
          poliza.fechaEmision
        ),

      vigenciaDesde:
        fechaInput(
          poliza.vigenciaDesde
        ),

      vigenciaHasta:
        fechaInput(
          poliza.vigenciaHasta
        ),

      estado:
        poliza.estado ||
        "VIGENTE",

      moneda:
        poliza.moneda ||
        "ARS",

      observaciones:
        poliza.observaciones ||
        "",

      vehiculos:
        Array.isArray(
          poliza.vehiculos
        ) &&
        poliza.vehiculos.length >
          0
          ? poliza.vehiculos.map(
              (item) => {
                const vehiculoObj =
                  typeof item.vehiculo ===
                    "object"
                    ? item.vehiculo
                    : null;

                const vehiculoId =
                  vehiculoObj?._id ||
                  item.vehiculo ||
                  "";

                return {
                  vehiculo:
                    vehiculoId,

                  patente:
                    item.patente ||
                    "",

                  descripcionSeguro:
                    item.descripcionSeguro ||
                    "",

                  marcaModeloSeguro:
                    item.marcaModeloSeguro ||
                    "",

                  cobertura:
                    item.cobertura ||
                    "",

                  sumaAsegurada:
                    item.sumaAsegurada ??
                    "",

                  premio:
                    item.premio ??
                    "",

                  observaciones:
                    item.observaciones ||
                    "",

                  estadoVinculacion:
                    vehiculoId
                      ? "VINCULADO"
                      : "NO_ENCONTRADO",

                  vehiculoInfo:
                    vehiculoObj,
                };
              }
            )
          : [
              vehiculoVacio(),
            ],
    });
  }, [show, poliza]);

  /* =======================================================
     CAMBIO GENERAL
  ======================================================= */

  const change = (e) => {
    const {
      name,
      value,
    } = e.target;

    setForm((prev) => ({
      ...prev,

      [name]: value,
    }));
  };

  /* =======================================================
     CAMBIO DE VEHÍCULO

     Si cambia patente:
     vuelve a buscar el vehículo correcto.
  ======================================================= */

  const changeVehiculo = (
    index,
    campo,
    valor
  ) => {
    setForm((prev) => {
      const vehiculos =
        [
          ...prev.vehiculos,
        ];

      const actual = {
        ...vehiculos[index],

        [campo]: valor,
      };

      if (
        campo ===
        "patente"
      ) {
        const patente =
          normalizarPatenteSeguro(
            valor
          );

        const camion =
          camiones.find(
            (item) =>
              normalizarPatenteSeguro(
                item.patente
              ) === patente
          );

        actual.patente =
          patente;

        actual.vehiculo =
          camion?._id ||
          "";

        actual.vehiculoInfo =
          camion ||
          null;

        actual.estadoVinculacion =
          patente
            ? camion
              ? "VINCULADO"
              : "NO_ENCONTRADO"
            : "";
      }

      vehiculos[index] =
        actual;

      return {
        ...prev,

        vehiculos,
      };
    });
  };

  /* =======================================================
     AGREGAR / QUITAR VEHÍCULO
  ======================================================= */

  const agregarVehiculo =
    () => {
      setForm((prev) => ({
        ...prev,

        vehiculos: [
          ...prev.vehiculos,
          vehiculoVacio(),
        ],
      }));
    };

  const quitarVehiculo = (
    index
  ) => {
    setForm((prev) => {
      const vehiculos =
        prev.vehiculos.filter(
          (_, i) =>
            i !== index
        );

      return {
        ...prev,

        vehiculos:
          vehiculos.length >
          0
            ? vehiculos
            : [
                vehiculoVacio(),
              ],
      };
    });
  };

  /* =======================================================
     TOTALES
  ======================================================= */

  const totalPremio =
    useMemo(
      () =>
        Number(
          form.vehiculos
            .reduce(
              (
                acc,
                item
              ) =>
                acc +
                n(
                  item.premio
                ),
              0
            )
            .toFixed(2)
        ),
      [form.vehiculos]
    );

  const totalSumaAsegurada =
    useMemo(
      () =>
        Number(
          form.vehiculos
            .reduce(
              (
                acc,
                item
              ) =>
                acc +
                n(
                  item.sumaAsegurada
                ),
              0
            )
            .toFixed(2)
        ),
      [form.vehiculos]
    );

  /* =======================================================
     IMPORTAR EXCEL
  ======================================================= */

  const importarExcelSeguro =
    async (archivo) => {
      if (!archivo) {
        return;
      }

      setError("");

      setInfoImportacion(
        null
      );

      setImportandoExcel(
        true
      );

      try {
        /*
          Si por algún motivo Camiones todavía
          no terminó de cargar, lo buscamos aquí.
        */

        let listaCamiones =
          camiones;

        if (
          listaCamiones.length ===
          0
        ) {
          const respuesta =
            await getCamionesParaSeguros();

          if (
            respuesta?.ok &&
            Array.isArray(
              respuesta.camiones
            )
          ) {
            listaCamiones =
              respuesta.camiones;

            setCamiones(
              listaCamiones
            );
          }
        }

        const buffer =
          await archivo.arrayBuffer();

        const workbook =
          XLSX.read(
            buffer,
            {
              type: "array",
            }
          );

        const nombreHoja =
          workbook
            .SheetNames?.[0];

        if (!nombreHoja) {
          throw new Error(
            "El Excel no contiene hojas."
          );
        }

        const hoja =
          workbook.Sheets[
            nombreHoja
          ];

        const filas =
          XLSX.utils.sheet_to_json(
            hoja,
            {
              header: 1,

              defval: "",

              raw: true,
            }
          );

        if (
          !Array.isArray(
            filas
          ) ||
          filas.length ===
            0
        ) {
          throw new Error(
            "El Excel está vacío."
          );
        }

        /* -----------------------------------------------
           BUSCAR CABECERA
        ----------------------------------------------- */

        const indiceEncabezado =
          filas.findIndex(
            (fila) => {
              const claves =
                (
                  fila ||
                  []
                ).map(
                  normalizarClaveExcel
                );

              return (
                claves.includes(
                  "PATENTE"
                ) &&
                claves.includes(
                  "COBERTURA"
                ) &&
                claves.includes(
                  "PREMIO"
                )
              );
            }
          );

        if (
          indiceEncabezado <
          0
        ) {
          throw new Error(
            "No se encontró la fila de encabezados del detalle de vehículos."
          );
        }

        const encabezados =
          filas[
            indiceEncabezado
          ].map(
            normalizarClaveExcel
          );

        const indice = (
          nombre
        ) =>
          encabezados.indexOf(
            normalizarClaveExcel(
              nombre
            )
          );

        const iPatente =
          indice(
            "PATENTE"
          );

        const iVehiculo =
          indice(
            "VEHICULO"
          );

        const iMarcaModelo =
          indice(
            "MARCA MODELO"
          );

        const iCobertura =
          indice(
            "COBERTURA"
          );

        const iSuma =
          indice(
            "SUMA ASEGURADA"
          );

        const iPremio =
          indice(
            "PREMIO"
          );

        /* -----------------------------------------------
           CABECERA DEL EXCEL
        ----------------------------------------------- */

        let aseguradoDetectado =
          "";

        let nroPolizaDetectado =
          "";

        let periodoDetectado =
          null;

        filas
          .slice(
            0,
            indiceEncabezado
          )
          .forEach(
            (fila) => {
              (
                fila ||
                []
              ).forEach(
                (celda) => {
                  const valor =
                    String(
                      celda ||
                        ""
                    ).trim();

                  if (
                    !valor
                  ) {
                    return;
                  }

                  if (
                    /^POLIZA\s+/i.test(
                      valor
                    )
                  ) {
                    nroPolizaDetectado =
                      valor
                        .replace(
                          /^POLIZA\s+/i,
                          ""
                        )
                        .trim();

                    return;
                  }

                  if (
                    /^VIGENCIA\s+/i.test(
                      valor
                    )
                  ) {
                    periodoDetectado =
                      extraerPeriodoExcel(
                        valor
                      );

                    return;
                  }

                  /*
                    Tomamos el primer texto útil
                    como candidato a asegurado.
                  */

                  if (
                    !aseguradoDetectado &&
                    !/^POLIZA/i.test(
                      valor
                    ) &&
                    !/^VIGENCIA/i.test(
                      valor
                    )
                  ) {
                    aseguradoDetectado =
                      valor;
                  }
                }
              );
            }
          );

        /* -----------------------------------------------
           MAPA CAMIONES
        ----------------------------------------------- */

        const mapaCamiones =
          new Map();

        listaCamiones.forEach(
          (camion) => {
            const patente =
              normalizarPatenteSeguro(
                camion.patente
              );

            if (
              patente
            ) {
              mapaCamiones.set(
                patente,
                camion
              );
            }
          }
        );

        /* -----------------------------------------------
           VEHÍCULOS DEL EXCEL
        ----------------------------------------------- */

        const vehiculosImportados =
          [];

        filas
          .slice(
            indiceEncabezado +
              1
          )
          .forEach(
            (fila) => {
              const patente =
                normalizarPatenteSeguro(
                  fila?.[
                    iPatente
                  ]
                );

              /*
                Filas totales o vacías.
              */

              if (
                !patente
              ) {
                return;
              }

              const camion =
                mapaCamiones.get(
                  patente
                );

              vehiculosImportados.push(
                {
                  vehiculo:
                    camion?._id ||
                    "",

                  patente,

                  descripcionSeguro:
                    String(
                      fila?.[
                        iVehiculo
                      ] || ""
                    ).trim(),

                  marcaModeloSeguro:
                    String(
                      fila?.[
                        iMarcaModelo
                      ] || ""
                    ).trim(),

                  cobertura:
                    String(
                      fila?.[
                        iCobertura
                      ] || ""
                    ).trim(),

                  sumaAsegurada:
                    numeroExcelSeguro(
                      fila?.[
                        iSuma
                      ]
                    ),

                  premio:
                    numeroExcelSeguro(
                      fila?.[
                        iPremio
                      ]
                    ),

                  observaciones:
                    "",

                  estadoVinculacion:
                    camion
                      ? "VINCULADO"
                      : "NO_ENCONTRADO",

                  vehiculoInfo:
                    camion ||
                    null,
                }
              );
            }
          );

        if (
          vehiculosImportados.length ===
          0
        ) {
          throw new Error(
            "No se encontraron vehículos para importar."
          );
        }

        /* -----------------------------------------------
           VALIDAR PATENTES DUPLICADAS EN EXCEL
        ----------------------------------------------- */

        const patentes =
          new Set();

        const patenteDuplicada =
          vehiculosImportados.find(
            (item) => {
              if (
                patentes.has(
                  item.patente
                )
              ) {
                return true;
              }

              patentes.add(
                item.patente
              );

              return false;
            }
          );

        if (
          patenteDuplicada
        ) {
          throw new Error(
            `La patente ${patenteDuplicada.patente} aparece repetida en el Excel.`
          );
        }

        /* -----------------------------------------------
           RESUMEN
        ----------------------------------------------- */

        const vinculados =
          vehiculosImportados.filter(
            (item) =>
              item.estadoVinculacion ===
              "VINCULADO"
          ).length;

        const noEncontrados =
          vehiculosImportados.length -
          vinculados;

        const totalPremioImportado =
          Number(
            vehiculosImportados
              .reduce(
                (
                  acc,
                  item
                ) =>
                  acc +
                  Number(
                    item.premio ||
                      0
                  ),
                0
              )
              .toFixed(2)
          );

        const totalSumaImportada =
          Number(
            vehiculosImportados
              .reduce(
                (
                  acc,
                  item
                ) =>
                  acc +
                  Number(
                    item.sumaAsegurada ||
                      0
                  ),
                0
              )
              .toFixed(2)
          );

        /* -----------------------------------------------
           COMPLETAR FORMULARIO
        ----------------------------------------------- */

        setForm(
          (prev) => ({
            ...prev,

            asegurado:
              prev.asegurado ||
              aseguradoDetectado,

            nroPoliza:
              prev.nroPoliza ||
              nroPolizaDetectado,

            vehiculos:
              vehiculosImportados,
          })
        );

        setInfoImportacion(
          {
            archivo:
              archivo.name,

            cantidad:
              vehiculosImportados.length,

            vinculados,

            noEncontrados,

            periodoDetectado,

            totalPremio:
              totalPremioImportado,

            totalSumaAsegurada:
              totalSumaImportada,
          }
        );
      } catch (err) {
        console.error(
          "Error importando seguro:",
          err
        );

        setError(
          err?.message ||
            "No se pudo analizar el Excel."
        );
      } finally {
        setImportandoExcel(
          false
        );
      }
    };

  /* =======================================================
     GUARDAR PÓLIZA
  ======================================================= */

  const guardar = async (
    e
  ) => {
    e.preventDefault();

    setError("");

    if (
      !form.compania.trim()
    ) {
      setError(
        "Debe indicar la compañía aseguradora."
      );

      return;
    }

    if (
      !form.nroPoliza.trim()
    ) {
      setError(
        "Debe indicar el número de póliza."
      );

      return;
    }

    if (
      !form.asegurado.trim()
    ) {
      setError(
        "Debe indicar el asegurado."
      );

      return;
    }

    if (
      !form.vigenciaDesde ||
      !form.vigenciaHasta
    ) {
      setError(
        "Debe indicar la vigencia de la póliza."
      );

      return;
    }

    const vehiculosValidos =
      form.vehiculos
        .filter(
          (item) =>
            String(
              item.patente ||
                ""
            ).trim()
        )
        .map(
          (item) => ({
            vehiculo:
              item.vehiculo ||
              undefined,

            patente:
              normalizarPatenteSeguro(
                item.patente
              ),

            descripcionSeguro:
              item.descripcionSeguro,

            marcaModeloSeguro:
              item.marcaModeloSeguro,

            cobertura:
              item.cobertura,

            sumaAsegurada:
              n(
                item.sumaAsegurada
              ),

            premio:
              n(
                item.premio
              ),

            observaciones:
              item.observaciones,
          })
        );

    const payload = {
      compania:
        form.compania,

      nroPoliza:
        form.nroPoliza,

      ramo:
        form.ramo,

      asegurado:
        form.asegurado,

      cuitAsegurado:
        form.cuitAsegurado,

      domicilioAsegurado:
        form.domicilioAsegurado,

      productor:
        form.productor,

      codigoProductor:
        form.codigoProductor,

      fechaEmision:
        form.fechaEmision ||
        null,

      vigenciaDesde:
        form.vigenciaDesde,

      vigenciaHasta:
        form.vigenciaHasta,

      estado:
        form.estado,

      moneda:
        form.moneda,

      observaciones:
        form.observaciones,

      vehiculos:
        vehiculosValidos,
    };

    setSaving(true);

    try {
      const respuesta =
        editando
          ? await editarPolizaSeguro(
              poliza._id,
              payload
            )
          : await crearPolizaSeguro(
              payload
            );

      if (
        !respuesta?.ok
      ) {
        setError(
          respuesta?.error ||
            "No se pudo guardar la póliza."
        );

        return;
      }

      if (
        typeof onSaved ===
        "function"
      ) {
        await onSaved();
      }

      onHide();
    } catch (err) {
      console.error(err);

      setError(
        err?.message ||
          "Error al guardar la póliza."
      );
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================
     JSX MODAL
  ======================================================= */

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      backdrop="static"
    >
      <Form
        onSubmit={guardar}
      >
        <Modal.Header
          closeButton
        >
          <Modal.Title>
            {editando
              ? "Editar póliza de seguro"
              : "Nueva póliza de seguro"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && (
            <Alert variant="danger">
              {error}
            </Alert>
          )}

          {/* DATOS PRINCIPALES */}

          <Row>
            <Col
              md={3}
              className="mb-3"
            >
              <Form.Label>
                Compañía
              </Form.Label>

              <Form.Control
                name="compania"
                value={
                  form.compania
                }
                onChange={
                  change
                }
                placeholder="Compañía aseguradora"
                required
              />
            </Col>

            <Col
              md={3}
              className="mb-3"
            >
              <Form.Label>
                N° póliza
              </Form.Label>

              <Form.Control
                name="nroPoliza"
                value={
                  form.nroPoliza
                }
                onChange={
                  change
                }
                required
              />
            </Col>

            <Col
              md={3}
              className="mb-3"
            >
              <Form.Label>
                Ramo
              </Form.Label>

              <Form.Control
                name="ramo"
                value={
                  form.ramo
                }
                onChange={
                  change
                }
              />
            </Col>

            <Col
              md={3}
              className="mb-3"
            >
              <Form.Label>
                Estado
              </Form.Label>

              <Form.Select
                name="estado"
                value={
                  form.estado
                }
                onChange={
                  change
                }
              >
                <option value="VIGENTE">
                  Vigente
                </option>

                <option value="PENDIENTE">
                  Pendiente
                </option>

                <option value="ANULADA">
                  Anulada
                </option>

                <option value="REEMPLAZADA">
                  Reemplazada
                </option>
              </Form.Select>
            </Col>
          </Row>

          {/* ASEGURADO */}

          <Row>
            <Col
              md={4}
              className="mb-3"
            >
              <Form.Label>
                Asegurado
              </Form.Label>

              <Form.Control
                name="asegurado"
                value={
                  form.asegurado
                }
                onChange={
                  change
                }
                required
              />
            </Col>

            <Col
              md={4}
              className="mb-3"
            >
              <Form.Label>
                CUIT asegurado
              </Form.Label>

              <Form.Control
                name="cuitAsegurado"
                value={
                  form.cuitAsegurado
                }
                onChange={
                  change
                }
              />
            </Col>

            <Col
              md={4}
              className="mb-3"
            >
              <Form.Label>
                Domicilio
              </Form.Label>

              <Form.Control
                name="domicilioAsegurado"
                value={
                  form.domicilioAsegurado
                }
                onChange={
                  change
                }
              />
            </Col>
          </Row>

          {/* VIGENCIA */}

          <Row>
            <Col
              md={3}
              className="mb-3"
            >
              <Form.Label>
                Emisión
              </Form.Label>

              <Form.Control
                type="date"
                name="fechaEmision"
                value={
                  form.fechaEmision
                }
                onChange={
                  change
                }
              />
            </Col>

            <Col
              md={3}
              className="mb-3"
            >
              <Form.Label>
                Vigencia desde
              </Form.Label>

              <Form.Control
                type="date"
                name="vigenciaDesde"
                value={
                  form.vigenciaDesde
                }
                onChange={
                  change
                }
                required
              />
            </Col>

            <Col
              md={3}
              className="mb-3"
            >
              <Form.Label>
                Vigencia hasta
              </Form.Label>

              <Form.Control
                type="date"
                name="vigenciaHasta"
                value={
                  form.vigenciaHasta
                }
                onChange={
                  change
                }
                required
              />
            </Col>

            <Col
              md={3}
              className="mb-3"
            >
              <Form.Label>
                Moneda
              </Form.Label>

              <Form.Select
                name="moneda"
                value={
                  form.moneda
                }
                onChange={
                  change
                }
              >
                <option value="ARS">
                  ARS
                </option>

                <option value="USD">
                  USD
                </option>
              </Form.Select>
            </Col>
          </Row>

          {/* PRODUCTOR */}

          <Row>
            <Col
              md={4}
              className="mb-3"
            >
              <Form.Label>
                Productor / asesor
              </Form.Label>

              <Form.Control
                name="productor"
                value={
                  form.productor
                }
                onChange={
                  change
                }
              />
            </Col>

            <Col
              md={3}
              className="mb-3"
            >
              <Form.Label>
                Código productor
              </Form.Label>

              <Form.Control
                name="codigoProductor"
                value={
                  form.codigoProductor
                }
                onChange={
                  change
                }
              />
            </Col>

            <Col
              md={5}
              className="mb-3"
            >
              <Form.Label>
                Observaciones
              </Form.Label>

              <Form.Control
                name="observaciones"
                value={
                  form.observaciones
                }
                onChange={
                  change
                }
              />
            </Col>
          </Row>

          <hr />

          {/* VEHÍCULOS */}

          <div
            className="d-flex justify-content-between align-items-center mb-2"
          >
            <div>
              <h5 className="mb-0">
                Vehículos asegurados
              </h5>

              <small className="text-muted">
                La patente se vincula automáticamente con Camiones.
              </small>
            </div>

            <div className="d-flex gap-2">
              <input
                id="archivo-seguro"
                type="file"
                accept=".xlsx,.xls"
                style={{
                  display:
                    "none",
                }}
                onChange={(
                  e
                ) => {
                  const archivo =
                    e.target
                      .files?.[0];

                  importarExcelSeguro(
                    archivo
                  );

                  e.target.value =
                    "";
                }}
              />

              <Button
                type="button"
                variant="outline-success"
                size="sm"
                disabled={
                  importandoExcel ||
                  cargandoCamiones
                }
                onClick={() =>
                  document
                    .getElementById(
                      "archivo-seguro"
                    )
                    ?.click()
                }
              >
                <FaFileExcel className="me-1" />

                {importandoExcel
                  ? "Analizando..."
                  : cargandoCamiones
                  ? "Cargando vehículos..."
                  : "Importar Excel"}
              </Button>

              <Button
                type="button"
                variant="outline-primary"
                size="sm"
                onClick={
                  agregarVehiculo
                }
              >
                <FaPlus className="me-1" />

                Agregar vehículo
              </Button>
            </div>
          </div>

          {/* RESULTADO DE IMPORTACIÓN */}

          {infoImportacion && (
            <Alert
              variant={
                infoImportacion.noEncontrados >
                0
                  ? "warning"
                  : "success"
              }
              className="mb-3"
            >
              <div className="fw-bold mb-1">
                Excel analizado:{" "}
                {
                  infoImportacion.archivo
                }
              </div>

              <div>
                Vehículos encontrados:{" "}
                <strong>
                  {
                    infoImportacion.cantidad
                  }
                </strong>

                {" | "}

                Vinculados con Danlu:{" "}
                <strong>
                  {
                    infoImportacion.vinculados
                  }
                </strong>

                {" | "}

                No encontrados:{" "}
                <strong>
                  {
                    infoImportacion.noEncontrados
                  }
                </strong>
              </div>

              {infoImportacion.periodoDetectado && (
                <div className="mt-1">
                  Período informado por el Excel:{" "}

                  <strong>
                    {
                      infoImportacion
                        .periodoDetectado
                        .desde
                    }{" "}
                    al{" "}
                    {
                      infoImportacion
                        .periodoDetectado
                        .hasta
                    }
                  </strong>

                  <div>
                    <small>
                      Este período no modifica automáticamente la vigencia general de la póliza.
                    </small>
                  </div>
                </div>
              )}

              <div className="mt-1">
                Suma asegurada:{" "}

                <strong>
                  {money(
                    infoImportacion.totalSumaAsegurada
                  )}
                </strong>

                {" | "}

                Premio detalle:{" "}

                <strong>
                  {money(
                    infoImportacion.totalPremio
                  )}
                </strong>
              </div>
            </Alert>
          )}

          {/* TABLA VEHÍCULOS */}

          <div
            style={{
              overflowX:
                "auto",
            }}
          >
            <Table
              bordered
              size="sm"
              responsive
            >
              <thead>
                <tr>
                  <th>
                    Patente
                  </th>

                  <th
                    style={{
                      width:
                        "115px",
                    }}
                  >
                    Estado
                  </th>

                  <th>
                    Tipo / descripción
                  </th>

                  <th>
                    Marca - modelo
                  </th>

                  <th>
                    Cobertura
                  </th>

                  <th>
                    Suma asegurada
                  </th>

                  <th>
                    Premio
                  </th>

                  <th
                    style={{
                      width:
                        "50px",
                    }}
                  />
                </tr>
              </thead>

              <tbody>
                {form.vehiculos.map(
                  (
                    item,
                    index
                  ) => (
                    <tr
                      key={
                        index
                      }
                    >
                      <td>
                        <Form.Control
                          size="sm"
                          value={
                            item.patente
                          }
                          onChange={(
                            e
                          ) =>
                            changeVehiculo(
                              index,
                              "patente",
                              e
                                .target
                                .value
                            )
                          }
                        />
                      </td>

                      <td className="text-center align-middle">
                        {item.estadoVinculacion ===
                        "VINCULADO" ? (
                          <Badge bg="success">
                            Vinculado
                          </Badge>
                        ) : item.estadoVinculacion ===
                          "NO_ENCONTRADO" ? (
                          <Badge bg="warning">
                            No encontrado
                          </Badge>
                        ) : (
                          <Badge bg="secondary">
                            Manual
                          </Badge>
                        )}
                      </td>

                      <td>
                        <Form.Control
                          size="sm"
                          value={
                            item.descripcionSeguro
                          }
                          onChange={(
                            e
                          ) =>
                            changeVehiculo(
                              index,
                              "descripcionSeguro",
                              e
                                .target
                                .value
                            )
                          }
                        />
                      </td>

                      <td>
                        <Form.Control
                          size="sm"
                          value={
                            item.marcaModeloSeguro
                          }
                          onChange={(
                            e
                          ) =>
                            changeVehiculo(
                              index,
                              "marcaModeloSeguro",
                              e
                                .target
                                .value
                            )
                          }
                        />
                      </td>

                      <td>
                        <Form.Control
                          size="sm"
                          value={
                            item.cobertura
                          }
                          onChange={(
                            e
                          ) =>
                            changeVehiculo(
                              index,
                              "cobertura",
                              e
                                .target
                                .value
                            )
                          }
                        />
                      </td>

                      <td>
                        <Form.Control
                          size="sm"
                          type="number"
                          step="0.01"
                          value={
                            item.sumaAsegurada
                          }
                          onChange={(
                            e
                          ) =>
                            changeVehiculo(
                              index,
                              "sumaAsegurada",
                              e
                                .target
                                .value
                            )
                          }
                        />
                      </td>

                      <td>
                        <Form.Control
                          size="sm"
                          type="number"
                          step="0.01"
                          value={
                            item.premio
                          }
                          onChange={(
                            e
                          ) =>
                            changeVehiculo(
                              index,
                              "premio",
                              e
                                .target
                                .value
                            )
                          }
                        />
                      </td>

                      <td className="text-center">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline-danger"
                          onClick={() =>
                            quitarVehiculo(
                              index
                            )
                          }
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </Table>
          </div>

          {/* TOTALES */}

          <Row className="mt-2">
            <Col md={6}>
              <div className="border rounded p-2">
                <small className="text-muted">
                  Suma asegurada total
                </small>

                <div className="fw-bold">
                  {money(
                    totalSumaAsegurada
                  )}
                </div>
              </div>
            </Col>

            <Col md={6}>
              <div className="border rounded p-2">
                <small className="text-muted">
                  Premio detalle vehículos
                </small>

                <div className="fw-bold">
                  {money(
                    totalPremio
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={onHide}
            disabled={
              saving
            }
          >
            Cancelar
          </Button>

          <Button
            type="submit"
            variant="primary"
            disabled={
              saving
            }
          >
            {saving
              ? "Guardando..."
              : "Guardar póliza"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

/* =========================================================
   MODAL PAGO DE SEGURO
========================================================= */

function ModalPagoSeguro({
  show,
  onHide,
  cuota,
  poliza,
  onSaved,
}) {
  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    cajas,
    setCajas,
  ] = useState([]);

  const [
    cuentas,
    setCuentas,
  ] = useState([]);

  const [
    cartera,
    setCartera,
  ] = useState([]);

  const [
    form,
    setForm,
  ] = useState({
    medioPago: "CAJA",

    fechaPago:
      hoyInput(),

    caja: "",

    cuentaBancaria: "",

    usarCartera:
      true,

    carteraItem: "",

    nroCheque: "",

    idICheq: "",

    banco: "",

    fechaCheque: "",

    fechaLiquidacion:
      "",
  });

  useEffect(() => {
    if (!show) {
      return;
    }

    setError("");

    setForm({
      medioPago: "CAJA",

      fechaPago:
        hoyInput(),

      caja: "",

      cuentaBancaria: "",

      usarCartera:
        true,

      carteraItem: "",

      nroCheque: "",

      idICheq: "",

      banco: "",

      fechaCheque: "",

      fechaLiquidacion:
        "",
    });

    setLoading(true);

    Promise.all([
      getCajas(),

      getCuentas(),

      getCarteraDisponibles({
        estados: [
          "EN_CARTERA",
        ],
      }),
    ])
      .then(
        ([
          rCajas,
          rCuentas,
          rCartera,
        ]) => {
          setCajas(
            Array.isArray(
              rCajas
            )
              ? rCajas
              : rCajas?.cajas ||
                  []
          );

          setCuentas(
            Array.isArray(
              rCuentas
            )
              ? rCuentas
              : rCuentas?.cuentas ||
                  []
          );

          setCartera(
            normalizarItemsCartera(
              rCartera
            )
          );
        }
      )
      .catch((err) => {
        console.error(err);

        setError(
          "No se pudieron cargar Caja/Banco/Cartera."
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [show]);

  const carteraFiltrada =
    useMemo(() => {
      if (
        form.medioPago ===
        "CHEQUE"
      ) {
        return cartera.filter(
          (item) =>
            item.tipo ===
            "CHEQUE"
        );
      }

      if (
        form.medioPago ===
        "ECHEQ"
      ) {
        return cartera.filter(
          (item) =>
            item.tipo ===
            "ICHEQ"
        );
      }

      return [];
    }, [
      cartera,
      form.medioPago,
    ]);

  const change = (e) => {
    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    setForm((prev) => ({
      ...prev,

      [name]:
        type ===
        "checkbox"
          ? checked
          : value,
    }));
  };

  const guardar =
    async () => {
      setError("");

      if (!cuota?._id) {
        return;
      }

      const payload = {
        medioPago:
          form.medioPago,

        fechaPago:
          form.fechaPago,

        importe:
          n(cuota.importe),
      };

      if (
        form.medioPago ===
        "CAJA"
      ) {
        if (!form.caja) {
          setError(
            "Seleccione una caja."
          );
          return;
        }

        payload.caja =
          form.caja;
      }

      if (
        form.medioPago ===
        "CUENTA_BANCARIA"
      ) {
        if (
          !form.cuentaBancaria
        ) {
          setError(
            "Seleccione una cuenta bancaria."
          );
          return;
        }

        payload.cuentaBancaria =
          form.cuentaBancaria;
      }

      if (
        form.medioPago ===
          "CHEQUE" ||
        form.medioPago ===
          "ECHEQ"
      ) {
        if (
          form.usarCartera
        ) {
          if (
            !form.carteraItem
          ) {
            setError(
              "Seleccione un valor de cartera."
            );
            return;
          }

          payload.carteraItem =
            form.carteraItem;
        } else if (
          form.medioPago ===
          "CHEQUE"
        ) {
          if (
            !form.nroCheque.trim()
          ) {
            setError(
              "Ingrese el número de cheque."
            );
            return;
          }

          payload.nroCheque =
            form.nroCheque;

          payload.banco =
            form.banco;

          payload.fechaCheque =
            form.fechaCheque ||
            form.fechaPago;
        } else {
          if (
            !form.idICheq.trim()
          ) {
            setError(
              "Ingrese el identificador del eCheq."
            );
            return;
          }

          payload.idICheq =
            form.idICheq;

          payload.banco =
            form.banco;

          payload.fechaLiquidacion =
            form.fechaLiquidacion ||
            form.fechaPago;
        }
      }

      const documento =
        cuota.nroDocumento ||
        (
          cuota.numeroCuota
            ? `Cuota ${cuota.numeroCuota}`
            : "documento"
        );

      if (
        !window.confirm(
          `¿Registrar el pago de ${money(
            cuota.importe
          )} correspondiente a ${documento}?`
        )
      ) {
        return;
      }

      setSaving(true);

      try {
        const respuesta =
          await pagarCuotaSeguro(
            cuota._id,
            payload
          );

        if (
          !respuesta?.ok
        ) {
          setError(
            respuesta?.error ||
              "No se pudo registrar el pago."
          );
          return;
        }

        if (
          typeof onSaved ===
          "function"
        ) {
          await onSaved();
        }

        onHide();
      } catch (err) {
        console.error(err);

        setError(
          err?.message ||
            "Error al registrar el pago."
        );
      } finally {
        setSaving(false);
      }
    };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      backdrop="static"
    >
      <Modal.Header
        closeButton
      >
        <Modal.Title>
          Registrar pago de seguro
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger">
            {error}
          </Alert>
        )}

        <div className="border rounded p-3 mb-3 bg-light">
          <Row>
            <Col md={8}>
              <strong>
                Documento
              </strong>

              <div>
                {poliza?.compania ||
                  "Seguro"}{" "}
                -{" "}
                {cuota?.nroDocumento ||
                  (
                    cuota?.numeroCuota
                      ? `Cuota ${cuota.numeroCuota}`
                      : "Sin número"
                  )}
              </div>

              <small className="text-muted">
                Póliza{" "}
                {poliza?.nroPoliza ||
                  "-"}
              </small>
            </Col>

            <Col
              md={4}
              className="text-md-end"
            >
              <strong>
                Importe
              </strong>

              <div className="fs-4 fw-bold">
                {money(
                  cuota?.importe
                )}
              </div>
            </Col>
          </Row>
        </div>

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            <Row>
              <Col
                md={6}
                className="mb-3"
              >
                <Form.Label>
                  Fecha de pago
                </Form.Label>

                <Form.Control
                  type="date"
                  name="fechaPago"
                  value={
                    form.fechaPago
                  }
                  onChange={
                    change
                  }
                />
              </Col>

              <Col
                md={6}
                className="mb-3"
              >
                <Form.Label>
                  Medio de pago
                </Form.Label>

                <Form.Select
                  name="medioPago"
                  value={
                    form.medioPago
                  }
                  onChange={
                    change
                  }
                >
                  <option value="CAJA">
                    Efectivo / Caja
                  </option>

                  <option value="CUENTA_BANCARIA">
                    Banco / transferencia
                  </option>

                  <option value="CHEQUE">
                    Cheque
                  </option>

                  <option value="ECHEQ">
                    eCheq
                  </option>
                </Form.Select>
              </Col>
            </Row>

            {form.medioPago ===
              "CAJA" && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Caja
                </Form.Label>

                <Form.Select
                  name="caja"
                  value={
                    form.caja
                  }
                  onChange={
                    change
                  }
                >
                  <option value="">
                    Seleccione...
                  </option>

                  {cajas.map(
                    (item) => (
                      <option
                        key={
                          item._id
                        }
                        value={
                          item._id
                        }
                      >
                        {getCajaLabel(
                          item
                        )}{" "}
                        - Saldo:{" "}
                        {money(
                          item.saldoActual
                        )}
                      </option>
                    )
                  )}
                </Form.Select>
              </Form.Group>
            )}

            {form.medioPago ===
              "CUENTA_BANCARIA" && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Cuenta bancaria
                </Form.Label>

                <Form.Select
                  name="cuentaBancaria"
                  value={
                    form.cuentaBancaria
                  }
                  onChange={
                    change
                  }
                >
                  <option value="">
                    Seleccione...
                  </option>

                  {cuentas.map(
                    (item) => (
                      <option
                        key={
                          item._id
                        }
                        value={
                          item._id
                        }
                      >
                        {getCuentaLabel(
                          item
                        )}{" "}
                        - Saldo:{" "}
                        {money(
                          item.saldoActual
                        )}
                      </option>
                    )
                  )}
                </Form.Select>
              </Form.Group>
            )}

            {(form.medioPago ===
              "CHEQUE" ||
              form.medioPago ===
                "ECHEQ") && (
              <>
                <Form.Check
                  type="switch"
                  className="mb-3"
                  name="usarCartera"
                  checked={
                    form.usarCartera
                  }
                  onChange={
                    change
                  }
                  label="Usar un cheque/eCheq que ya está en cartera"
                />

                {form.usarCartera ? (
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Valor disponible
                    </Form.Label>

                    <Form.Select
                      name="carteraItem"
                      value={
                        form.carteraItem
                      }
                      onChange={
                        change
                      }
                    >
                      <option value="">
                        Seleccione...
                      </option>

                      {carteraFiltrada.map(
                        (item) => (
                          <option
                            key={
                              item._id
                            }
                            value={
                              item._id
                            }
                          >
                            {item.nro ||
                              item.nroCheque ||
                              item.idICheq ||
                              "Sin número"}{" "}
                            -{" "}
                            {money(
                              item.importe
                            )}{" "}
                            -{" "}
                            {item.bancoEmisor ||
                              item.banco ||
                              ""}
                          </option>
                        )
                      )}
                    </Form.Select>

                    <small className="text-muted">
                      Solamente se podrá utilizar un valor cuyo importe coincida exactamente con el documento.
                    </small>
                  </Form.Group>
                ) : (
                  <>
                    {form.medioPago ===
                      "CHEQUE" && (
                      <Row>
                        <Col
                          md={6}
                          className="mb-3"
                        >
                          <Form.Label>
                            N° cheque
                          </Form.Label>

                          <Form.Control
                            name="nroCheque"
                            value={
                              form.nroCheque
                            }
                            onChange={
                              change
                            }
                          />
                        </Col>

                        <Col
                          md={6}
                          className="mb-3"
                        >
                          <Form.Label>
                            Fecha cheque
                          </Form.Label>

                          <Form.Control
                            type="date"
                            name="fechaCheque"
                            value={
                              form.fechaCheque
                            }
                            onChange={
                              change
                            }
                          />
                        </Col>
                      </Row>
                    )}

                    {form.medioPago ===
                      "ECHEQ" && (
                      <Row>
                        <Col
                          md={6}
                          className="mb-3"
                        >
                          <Form.Label>
                            ID eCheq
                          </Form.Label>

                          <Form.Control
                            name="idICheq"
                            value={
                              form.idICheq
                            }
                            onChange={
                              change
                            }
                          />
                        </Col>

                        <Col
                          md={6}
                          className="mb-3"
                        >
                          <Form.Label>
                            Fecha liquidación
                          </Form.Label>

                          <Form.Control
                            type="date"
                            name="fechaLiquidacion"
                            value={
                              form.fechaLiquidacion
                            }
                            onChange={
                              change
                            }
                          />
                        </Col>
                      </Row>
                    )}

                    <Form.Group className="mb-3">
                      <Form.Label>
                        Banco
                      </Form.Label>

                      <Form.Control
                        name="banco"
                        value={
                          form.banco
                        }
                        onChange={
                          change
                        }
                      />
                    </Form.Group>
                  </>
                )}
              </>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onHide}
          disabled={
            saving
          }
        >
          Cancelar
        </Button>

        <Button
          variant="success"
          onClick={
            guardar
          }
          disabled={
            saving ||
            loading
          }
        >
          <FaMoneyBillWave className="me-1" />

          {saving
            ? "Registrando..."
            : "Registrar pago"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/* =========================================================
   MODAL CUOTAS
========================================================= */

function ModalCuotas({
  show,
  onHide,
  poliza,
  onSaved,
}) {
  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    cuotas,
    setCuotas,
  ] = useState([]);

  const [
    procesandoPago,
    setProcesandoPago,
  ] = useState(false);

  const [
    modalPago,
    setModalPago,
  ] = useState({
    show: false,
    cuota: null,
  });

  const [
    form,
    setForm,
  ] = useState({
    tipoDocumento:
      "CUOTA",

    nroDocumento: "",

    numeroCuota: "",

    fechaEmision: "",

    periodoDesde: "",

    periodoHasta: "",

    vencimiento: "",

    importe: "",

    aliasTransferencia:
      "",

    codigoPago: "",

    codigoLinkPago: "",

    observaciones: "",
  });

  /* =======================================================
     CARGAR CUOTAS
  ======================================================= */

  const cargar =
    useCallback(
      async () => {
        if (
          !show ||
          !poliza?._id
        ) {
          return;
        }

        setLoading(true);

        setError("");

        try {
          const respuesta =
            await getPolizaSeguro(
              poliza._id
            );

          if (
            !respuesta?.ok
          ) {
            throw new Error(
              respuesta?.error ||
                "No se pudo cargar la póliza."
            );
          }

          setCuotas(
            Array.isArray(
              respuesta.cuotas
            )
              ? respuesta.cuotas
              : []
          );
        } catch (err) {
          setError(
            err?.message ||
              "No se pudieron cargar las cuotas."
          );
        } finally {
          setLoading(false);
        }
      },
      [
        show,
        poliza,
      ]
    );

  useEffect(() => {
    if (!show) {
      return;
    }

    setForm({
      tipoDocumento:
        "CUOTA",

      nroDocumento: "",

      numeroCuota: "",

      fechaEmision: "",

      periodoDesde: "",

      periodoHasta: "",

      vencimiento: "",

      importe: "",

      aliasTransferencia:
        "",

      codigoPago: "",

      codigoLinkPago: "",

      observaciones: "",
    });

    cargar();
  }, [
    show,
    cargar,
  ]);

  const change = (e) => {
    const {
      name,
      value,
    } = e.target;

    setForm((prev) => ({
      ...prev,

      [name]: value,
    }));
  };

  /* =======================================================
     GUARDAR CUOTA
  ======================================================= */

  const guardarCuota =
    async (e) => {
      e.preventDefault();

      setError("");

      if (
        !form.vencimiento
      ) {
        setError(
          "Debe indicar el vencimiento."
        );

        return;
      }

      if (
        n(form.importe) <=
        0
      ) {
        setError(
          "El importe debe ser mayor a cero."
        );

        return;
      }

      setSaving(true);

      try {
        const respuesta =
          await crearCuotaSeguro(
            {
              poliza:
                poliza._id,

              tipoDocumento:
                form.tipoDocumento,

              nroDocumento:
                form.nroDocumento,

              numeroCuota:
                form.numeroCuota
                  ? Number(
                      form.numeroCuota
                    )
                  : null,

              fechaEmision:
                form.fechaEmision ||
                null,

              periodoDesde:
                form.periodoDesde ||
                null,

              periodoHasta:
                form.periodoHasta ||
                null,

              vencimiento:
                form.vencimiento,

              importe:
                n(
                  form.importe
                ),

              moneda:
                poliza.moneda ||
                "ARS",

              aliasTransferencia:
                form.aliasTransferencia,

              codigoPago:
                form.codigoPago,

              codigoLinkPago:
                form.codigoLinkPago,

              observaciones:
                form.observaciones,
            }
          );

        if (
          !respuesta?.ok
        ) {
          setError(
            respuesta?.error ||
              "No se pudo crear la cuota."
          );

          return;
        }

        setForm({
          tipoDocumento:
            "CUOTA",

          nroDocumento: "",

          numeroCuota: "",

          fechaEmision: "",

          periodoDesde: "",

          periodoHasta: "",

          vencimiento: "",

          importe: "",

          aliasTransferencia:
            "",

          codigoPago: "",

          codigoLinkPago: "",

          observaciones: "",
        });

        await cargar();

        if (
          typeof onSaved ===
          "function"
        ) {
          await onSaved();
        }
      } catch (err) {
        setError(
          err?.message ||
            "Error al registrar la cuota."
        );
      } finally {
        setSaving(false);
      }
    };

  const refrescarDespuesPago =
    async () => {
      await cargar();

      if (
        typeof onSaved ===
        "function"
      ) {
        await onSaved();
      }
    };

  const anularPago =
    async (item) => {
      const documento =
        item.nroDocumento ||
        (
          item.numeroCuota
            ? `Cuota ${item.numeroCuota}`
            : "documento"
        );

      if (
        !window.confirm(
          `¿Seguro que desea ANULAR el pago de ${documento} por ${money(
            item.montoPagado ||
              item.importe
          )}?\n\nEsta operación devolverá el saldo a Caja/Banco o revertirá Cartera.`
        )
      ) {
        return;
      }

      setProcesandoPago(
        true
      );

      setError("");

      try {
        const respuesta =
          await anularPagoCuotaSeguro(
            item._id
          );

        if (
          !respuesta?.ok
        ) {
          setError(
            respuesta?.error ||
              "No se pudo anular el pago."
          );

          return;
        }

        await refrescarDespuesPago();
      } catch (err) {
        console.error(err);

        setError(
          err?.message ||
            "Error al anular el pago."
        );
      } finally {
        setProcesandoPago(
          false
        );
      }
    };

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size="xl"
        backdrop="static"
      >
      <Modal.Header
        closeButton
      >
        <Modal.Title>
          Facturación y vencimientos -{" "}
          {
            poliza?.nroPoliza
          }
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger">
            {error}
          </Alert>
        )}

        <div className="border rounded p-3 mb-3 bg-light">
          <Row>
            <Col md={4}>
              <strong>
                Compañía
              </strong>

              <div>
                {
                  poliza?.compania
                }
              </div>
            </Col>

            <Col md={4}>
              <strong>
                Asegurado
              </strong>

              <div>
                {
                  poliza?.asegurado
                }
              </div>
            </Col>

            <Col md={4}>
              <strong>
                Vigencia
              </strong>

              <div>
                {fechaCorta(
                  poliza?.vigenciaDesde
                )}{" "}
                al{" "}
                {fechaCorta(
                  poliza?.vigenciaHasta
                )}
              </div>
            </Col>
          </Row>
        </div>

        <Form
          onSubmit={
            guardarCuota
          }
        >
          <Row>
            <Col
              md={2}
              className="mb-2"
            >
              <Form.Label>
                Tipo
              </Form.Label>

              <Form.Select
                name="tipoDocumento"
                value={
                  form.tipoDocumento
                }
                onChange={
                  change
                }
              >
                <option value="CUOTA">
                  Cuota
                </option>

                <option value="FACTURACION">
                  Facturación
                </option>

                <option value="ENDOSO">
                  Endoso
                </option>

                <option value="AJUSTE">
                  Ajuste
                </option>

                <option value="OTRO">
                  Otro
                </option>
              </Form.Select>
            </Col>

            <Col
              md={2}
              className="mb-2"
            >
              <Form.Label>
                Documento
              </Form.Label>

              <Form.Control
                name="nroDocumento"
                value={
                  form.nroDocumento
                }
                onChange={
                  change
                }
              />
            </Col>

            <Col
              md={2}
              className="mb-2"
            >
              <Form.Label>
                N° cuota
              </Form.Label>

              <Form.Control
                type="number"
                name="numeroCuota"
                value={
                  form.numeroCuota
                }
                onChange={
                  change
                }
              />
            </Col>

            <Col
              md={2}
              className="mb-2"
            >
              <Form.Label>
                Emisión
              </Form.Label>

              <Form.Control
                type="date"
                name="fechaEmision"
                value={
                  form.fechaEmision
                }
                onChange={
                  change
                }
              />
            </Col>

            <Col
              md={2}
              className="mb-2"
            >
              <Form.Label>
                Vencimiento
              </Form.Label>

              <Form.Control
                type="date"
                name="vencimiento"
                value={
                  form.vencimiento
                }
                onChange={
                  change
                }
                required
              />
            </Col>

            <Col
              md={2}
              className="mb-2"
            >
              <Form.Label>
                Importe
              </Form.Label>

              <Form.Control
                type="number"
                step="0.01"
                name="importe"
                value={
                  form.importe
                }
                onChange={
                  change
                }
                required
              />
            </Col>
          </Row>

          <Row>
            <Col
              md={3}
              className="mb-2"
            >
              <Form.Label>
                Período desde
              </Form.Label>

              <Form.Control
                type="date"
                name="periodoDesde"
                value={
                  form.periodoDesde
                }
                onChange={
                  change
                }
              />
            </Col>

            <Col
              md={3}
              className="mb-2"
            >
              <Form.Label>
                Período hasta
              </Form.Label>

              <Form.Control
                type="date"
                name="periodoHasta"
                value={
                  form.periodoHasta
                }
                onChange={
                  change
                }
              />
            </Col>

            <Col
              md={3}
              className="mb-2"
            >
              <Form.Label>
                Alias transferencia
              </Form.Label>

              <Form.Control
                name="aliasTransferencia"
                value={
                  form.aliasTransferencia
                }
                onChange={
                  change
                }
              />
            </Col>

            <Col
              md={3}
              className="mb-2 d-flex align-items-end"
            >
              <Button
                type="submit"
                variant="primary"
                className="w-100"
                disabled={
                  saving
                }
              >
                <FaPlus className="me-1" />

                {saving
                  ? "Guardando..."
                  : "Agregar documento"}
              </Button>
            </Col>
          </Row>
        </Form>

        <hr />

        <h5>
          Documentos registrados
        </h5>

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
          </div>
        ) : (
          <Table
            striped
            bordered
            hover
            responsive
            size="sm"
          >
            <thead>
              <tr>
                <th>
                  Tipo
                </th>

                <th>
                  Documento
                </th>

                <th>
                  Cuota
                </th>

                <th>
                  Vencimiento
                </th>

                <th className="text-end">
                  Importe
                </th>

                <th className="text-center">
                  Estado
                </th>

                <th className="text-end">
                  Acciones
                </th>
              </tr>
            </thead>

            <tbody>
              {cuotas.map(
                (item) => (
                  <tr
                    key={
                      item._id
                    }
                  >
                    <td>
                      {
                        item.tipoDocumento
                      }
                    </td>

                    <td>
                      {item.nroDocumento ||
                        "-"}
                    </td>

                    <td>
                      {item.numeroCuota ||
                        "-"}
                    </td>

                    <td>
                      {fechaCorta(
                        item.vencimiento
                      )}
                    </td>

                    <td className="text-end fw-bold">
                      {money(
                        item.importe
                      )}
                    </td>

                    <td className="text-center">
                      <Badge
                        bg={estadoCuotaBadge(
                          item.estadoCalculado ||
                            item.estado
                        )}
                      >
                        {item.estadoCalculado ||
                          item.estado}
                      </Badge>
                    </td>

                    <td className="text-end">
                      {item.estado !==
                        "PAGADA" &&
                        item.estado !==
                          "ANULADA" && (
                          <Button
                            size="sm"
                            variant="success"
                            disabled={
                              procesandoPago
                            }
                            onClick={() =>
                              setModalPago({
                                show: true,
                                cuota: item,
                              })
                            }
                          >
                            <FaMoneyBillWave className="me-1" />
                            Pagar
                          </Button>
                        )}

                      {item.estado ===
                        "PAGADA" && (
                          <Button
                            size="sm"
                            variant="outline-danger"
                            disabled={
                              procesandoPago
                            }
                            onClick={() =>
                              anularPago(
                                item
                              )
                            }
                          >
                            <FaUndoAlt className="me-1" />
                            Anular pago
                          </Button>
                        )}
                    </td>
                  </tr>
                )
              )}

              {cuotas.length ===
                0 && (
                <tr>
                  <td
                    colSpan="7"
                    className="text-center text-muted py-3"
                  >
                    No hay documentos registrados.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onHide}
          disabled={
            procesandoPago
          }
        >
          Cerrar
        </Button>
      </Modal.Footer>
      </Modal>

      <ModalPagoSeguro
        show={
          modalPago.show
        }
        cuota={
          modalPago.cuota
        }
        poliza={
          poliza
        }
        onHide={() =>
          setModalPago({
            show: false,
            cuota: null,
          })
        }
        onSaved={
          refrescarDespuesPago
        }
      />
    </>
  );
}

/* =========================================================
   PAGE
========================================================= */

const Seguros = () => {
  const [
    polizas,
    setPolizas,
  ] = useState([]);

  const [
    cuotas,
    setCuotas,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    buscar,
    setBuscar,
  ] = useState("");

  const [
    filtroEstado,
    setFiltroEstado,
  ] = useState("");

  const [
    modalPoliza,
    setModalPoliza,
  ] = useState({
    show: false,

    poliza: null,
  });

  const [
    modalCuotas,
    setModalCuotas,
  ] = useState({
    show: false,

    poliza: null,
  });

  /* =======================================================
     CARGAR PANTALLA
  ======================================================= */

  const cargar =
    useCallback(
      async () => {
        setLoading(true);

        setError("");

        try {
          const params =
            {};

          if (
            buscar.trim()
          ) {
            params.q =
              buscar.trim();
          }

          if (
            filtroEstado
          ) {
            params.estado =
              filtroEstado;
          }

          const [
            rPolizas,
            rCuotas,
          ] =
            await Promise.all(
              [
                getPolizasSeguros(
                  params
                ),

                getCuotasSeguros(),
              ]
            );

          if (
            !rPolizas?.ok
          ) {
            throw new Error(
              rPolizas?.error ||
                "No se pudieron cargar las pólizas."
            );
          }

          if (
            !rCuotas?.ok
          ) {
            throw new Error(
              rCuotas?.error ||
                "No se pudieron cargar las cuotas."
            );
          }

          setPolizas(
            Array.isArray(
              rPolizas.polizas
            )
              ? rPolizas.polizas
              : []
          );

          setCuotas(
            Array.isArray(
              rCuotas.cuotas
            )
              ? rCuotas.cuotas
              : []
          );
        } catch (err) {
          console.error(
            err
          );

          setError(
            err?.message ||
              "Error cargando Seguros."
          );
        } finally {
          setLoading(false);
        }
      },
      [
        buscar,
        filtroEstado,
      ]
    );

  useEffect(() => {
    cargar();
  }, [cargar]);

  /* =======================================================
     KPIs
  ======================================================= */

  const vigentes =
    polizas.filter(
      (item) =>
        item.estadoCalculado ===
        "VIGENTE"
    ).length;

  const vencidas =
    polizas.filter(
      (item) =>
        item.estadoCalculado ===
        "VENCIDA"
    ).length;

  const proximas30 =
    useMemo(() => {
      const hoy =
        new Date();

      const limite =
        new Date();

      limite.setDate(
        limite.getDate() +
          30
      );

      return polizas.filter(
        (item) => {
          if (
            item.estadoCalculado !==
              "VIGENTE" ||
            !item.vigenciaHasta
          ) {
            return false;
          }

          const vence =
            new Date(
              item.vigenciaHasta
            );

          return (
            vence >= hoy &&
            vence <= limite
          );
        }
      ).length;
    }, [polizas]);

  const cuotasPendientes =
    cuotas.filter(
      (item) =>
        [
          "PENDIENTE",
          "VENCIDA",
        ].includes(
          item.estadoCalculado ||
            item.estado
        )
    );

  const totalCuotasPendientes =
    cuotasPendientes.reduce(
      (
        acc,
        item
      ) =>
        acc +
        n(
          item.importe
        ),
      0
    );

  return (
    <div
      className="container-fluid py-3"
      style={{
        backgroundColor:
          "#fff",

        minHeight:
          "100vh",
      }}
    >
      {/* CABECERA */}

      <div
        className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3"
      >
        <div>
          <h3 className="mb-0">
            <FaShieldAlt className="me-2" />

            Seguros de vehículos
          </h3>

          <small className="text-muted">
            Pólizas, coberturas, vehículos y vencimientos
          </small>
        </div>

        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            onClick={
              cargar
            }
          >
            <FaSyncAlt className="me-1" />

            Actualizar
          </Button>

          <Button
            variant="primary"
            onClick={() =>
              setModalPoliza(
                {
                  show: true,

                  poliza:
                    null,
                }
              )
            }
          >
            <FaPlus className="me-1" />

            Nueva póliza
          </Button>
        </div>
      </div>

      {/* KPIs */}

      <Row className="mb-3">
        <Col
          md={3}
          className="mb-2"
        >
          <div className="border rounded p-3">
            <small className="text-muted">
              Pólizas vigentes
            </small>

            <div className="fs-4 fw-bold">
              {vigentes}
            </div>
          </div>
        </Col>

        <Col
          md={3}
          className="mb-2"
        >
          <div className="border rounded p-3">
            <small className="text-muted">
              Vencidas
            </small>

            <div className="fs-4 fw-bold">
              {vencidas}
            </div>
          </div>
        </Col>

        <Col
          md={3}
          className="mb-2"
        >
          <div className="border rounded p-3">
            <small className="text-muted">
              Vencen en 30 días
            </small>

            <div className="fs-4 fw-bold">
              {proximas30}
            </div>
          </div>
        </Col>

        <Col
          md={3}
          className="mb-2"
        >
          <div className="border rounded p-3">
            <small className="text-muted">
              Cuotas pendientes
            </small>

            <div className="fs-5 fw-bold">
              {money(
                totalCuotasPendientes
              )}
            </div>

            <small>
              {
                cuotasPendientes.length
              }{" "}
              vencimiento(s)
            </small>
          </div>
        </Col>
      </Row>

      {/* FILTROS */}

      <div className="border rounded p-3 mb-3">
        <Row>
          <Col md={6}>
            <Form.Label>
              Buscar
            </Form.Label>

            <Form.Control
              value={
                buscar
              }
              onChange={(
                e
              ) =>
                setBuscar(
                  e.target
                    .value
                )
              }
              placeholder="Compañía, póliza, asegurado o patente"
            />
          </Col>

          <Col md={3}>
            <Form.Label>
              Estado
            </Form.Label>

            <Form.Select
              value={
                filtroEstado
              }
              onChange={(
                e
              ) =>
                setFiltroEstado(
                  e.target
                    .value
                )
              }
            >
              <option value="">
                Todos
              </option>

              <option value="VIGENTE">
                Vigente
              </option>

              <option value="VENCIDA">
                Vencida
              </option>

              <option value="PENDIENTE">
                Pendiente
              </option>

              <option value="ANULADA">
                Anulada
              </option>

              <option value="REEMPLAZADA">
                Reemplazada
              </option>
            </Form.Select>
          </Col>
        </Row>
      </div>

      {error && (
        <Alert variant="danger">
          {error}
        </Alert>
      )}

      {/* TABLA */}

      <div className="border rounded">
        <Table
          striped
          hover
          responsive
          className="mb-0"
        >
          <thead>
            <tr>
              <th>
                Compañía
              </th>

              <th>
                Póliza
              </th>

              <th>
                Asegurado
              </th>

              <th className="text-center">
                Vehículos
              </th>

              <th>
                Vigencia
              </th>

              <th className="text-center">
                Estado
              </th>

              <th className="text-end">
                Premio detalle
              </th>

              <th className="text-end">
                Acciones
              </th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan="8"
                  className="text-center py-5"
                >
                  <Spinner animation="border" />
                </td>
              </tr>
            ) : polizas.length ===
              0 ? (
              <tr>
                <td
                  colSpan="8"
                  className="text-center text-muted py-5"
                >
                  No hay pólizas registradas.
                </td>
              </tr>
            ) : (
              polizas.map(
                (item) => (
                  <tr
                    key={
                      item._id
                    }
                  >
                    <td className="fw-bold">
                      {
                        item.compania
                      }
                    </td>

                    <td>
                      {
                        item.nroPoliza
                      }
                    </td>

                    <td>
                      {
                        item.asegurado
                      }
                    </td>

                    <td className="text-center">
                      {Array.isArray(
                        item.vehiculos
                      )
                        ? item
                            .vehiculos
                            .length
                        : 0}
                    </td>

                    <td>
                      {fechaCorta(
                        item.vigenciaDesde
                      )}{" "}
                      al{" "}
                      {fechaCorta(
                        item.vigenciaHasta
                      )}
                    </td>

                    <td className="text-center">
                      <Badge
                        bg={estadoBadge(
                          item.estadoCalculado ||
                            item.estado
                        )}
                      >
                        {item.estadoCalculado ||
                          item.estado}
                      </Badge>
                    </td>

                    <td className="text-end fw-bold">
                      {money(
                        item.premioDetalleVehiculos
                      )}
                    </td>

                    <td className="text-end">
                      <div className="d-flex justify-content-end gap-1">
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          title="Editar póliza"
                          onClick={() =>
                            setModalPoliza(
                              {
                                show: true,

                                poliza:
                                  item,
                              }
                            )
                          }
                        >
                          <FaEdit />
                        </Button>

                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() =>
                            setModalCuotas(
                              {
                                show: true,

                                poliza:
                                  item,
                              }
                            )
                          }
                        >
                          <FaFileInvoiceDollar className="me-1" />

                          Facturación
                        </Button>
                      </div>
                    </td>
                  </tr>
                )
              )
            )}
          </tbody>
        </Table>
      </div>

      {/* MODALES */}

      <ModalPoliza
        show={
          modalPoliza.show
        }
        poliza={
          modalPoliza.poliza
        }
        onHide={() =>
          setModalPoliza({
            show: false,

            poliza:
              null,
          })
        }
        onSaved={
          cargar
        }
      />

      <ModalCuotas
        show={
          modalCuotas.show
        }
        poliza={
          modalCuotas.poliza
        }
        onHide={() =>
          setModalCuotas({
            show: false,

            poliza:
              null,
          })
        }
        onSaved={
          cargar
        }
      />
    </div>
  );
};

export default Seguros;